import {
  MutableRefObject,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { createStrictOptionalContext } from 'helpers/context';
import Presentational from './presentational';
import {
  ActionsType,
  EditFile,
  RightPanelControls,
  RotateFile,
} from './types';
import useHandleKey from 'hooks/keyPress';
import { MainDisplayedFile } from './mainDisplayedFile';
import { isOpenSelector, slideSelector } from './galleryStateStore';
import { DisplayedFile, FileSetForDisplay } from './filesStore';
import { DrawingType } from '../graphicUploader/types';
import { MainImage } from '../mainImage';
import { GalleryAction } from './galleryAction';

export { GalleryAction } from './galleryAction';

function clampPreviousSlideIndex(nextIndex: number): number {
  if (nextIndex < 0) {
    return 0;
  }
  return nextIndex;
}

function keyhandlerFilter(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement;
  const editableTarget =
    target.closest('input, textarea, [contenteditable="true"]') !==
      null || target.tagName === 'SELECT';

  return !editableTarget;
}

function registerKeyboardHandlers(
  changeGallery: (
    place: 'open' | 'slide',
    value: number | boolean
  ) => void,
  moveGallery: (direction: number) => void
): void {
  const ESC_KEY = 'Escape';
  const ARROW_RIGHT_KEY = 'ArrowRight';
  const ARROW_LEFT_KEY = 'ArrowLeft';
  useHandleKey(
    ESC_KEY,
    useCallback(() => changeGallery('open', false), [changeGallery]),
    { filter: keyhandlerFilter }
  );
  useHandleKey(
    ARROW_RIGHT_KEY,
    useCallback(() => moveGallery(1), [moveGallery]),
    { filter: keyhandlerFilter }
  );
  useHandleKey(
    ARROW_LEFT_KEY,
    useCallback(() => moveGallery(-1), [moveGallery]),
    { filter: keyhandlerFilter }
  );
}

export interface GalleryState {
  open: boolean;
  slide: number;
}

export interface GalleryContextType<K = object> {
  setFiles: (files: FileSetForDisplay<K>[]) => void;
  drawings: DrawingType[];
  addDrawing: (drawing: DrawingType, index: number) => void;
  changeGallery: (
    key: keyof GalleryState,
    value: GalleryState[keyof GalleryState]
  ) => void;
  setGallery: (state: GalleryState) => void;
  removeFile: (index: number) => void;
  rotateFile: RotateFile;
  editFile?: EditFile<K>;
  preview: boolean;
  useDisplayedFiles: <T>(
    selector: (store: DisplayedFile<K>[]) => T
  ) => [T, (value: DisplayedFile<K>[]) => void];
  useMainDisplayedFile: <T>(
    selector: (store: MainDisplayedFile<K> | undefined) => T
  ) => [T, (value: MainDisplayedFile<K>) => void];
  useGalleryState: <T>(
    selector: (store: GalleryState) => T
  ) => [T, (value: GalleryState) => void];
  disableMiniatures?: boolean;
  downloadFile: (file: DisplayedFile<K>) => void;
  refreshPreview?: () => Promise<boolean>;
  openEditComponent: () => void;
  EditComponent?: (...args: any[]) => ReactElement;
  editTitleId?: string;
  showActions: GalleryAction[];
  ImageDisplayOverlay?: () => ReactElement | null;
  MiniaturesOverlay?: () => ReactElement | null;
  RightPanel?: () => ReactElement;
  rightPanelControls?: RightPanelControls;
  skipAutoSlideHandling?: boolean;
  /** If true, PDF files will always show PdfThumbnail in miniatures (used in Documentation browser) */
  showPdfAsThumbnail?: boolean;
  fetchShareableUrl?: (documentId: string) => Promise<string>;
}

interface GalleryProps<K = object> {
  setFiles: (files: FileSetForDisplay<K>[]) => void;
  drawings: DrawingType[];
  addDrawing: (drawing: DrawingType, index: number) => void;
  changeGallery: (
    key: keyof GalleryState,
    value: GalleryState[keyof GalleryState]
  ) => void;
  setGallery: (state: GalleryState) => void;
  removeFile: (index: number) => void;
  rotateFile: RotateFile;
  editFile?: EditFile<K>;
  preview: boolean;
  useMainDisplayedFile: <T>(
    selector: (store: MainDisplayedFile<K> | undefined) => T
  ) => [T, (value: MainDisplayedFile<K>) => void];
  useGalleryState: <T>(
    selector: (store: GalleryState) => T
  ) => [T, (value: GalleryState) => void];
  useDisplayedFiles: <T>(
    selector: (store: DisplayedFile<K>[]) => T
  ) => [T, (value: DisplayedFile<K>[]) => void];
  downloadFile: (file: DisplayedFile<K>) => void;
  refreshPreview?: () => Promise<boolean>;
  disableMiniatures?: boolean;
  openEditComponent: () => void;
  EditComponent?: (...args: any[]) => ReactElement;
  editTitleId?: string;
  showActions: GalleryAction[];
  ImageDisplayOverlay?: () => ReactElement | null;
  MiniaturesOverlay?: () => ReactElement | null;
  mainImageHandlerRef?: MutableRefObject<MainImage> | undefined;
  RightPanel?: () => ReactElement;
  rightPanelControls?: RightPanelControls;
  skipAutoSlideHandling?: boolean;
  /** If true, PDF files will always show PdfThumbnail in miniatures (used in Documentation browser) */
  showPdfAsThumbnail?: boolean;
  fetchShareableUrl?: (documentId: string) => Promise<string>;
}
const { Provider: GalleryContextProvider, useRequiredContext: useAnyGalleryContext } =
  createStrictOptionalContext<GalleryContextType<any>>('useGalleryContext', 'Gallery');

function lengthSelector<K>(files: K[]): number {
  return files.length;
}

function undeletedIndexSelector<K extends { deleted?: boolean }>(
  files: K[]
): number {
  return files.findIndex((file: K) => !file.deleted);
}

function currentSlideFileSelector<K>(slide: number) {
  return function selectCurrentSlideFile(
    files: DisplayedFile<K>[]
  ): DisplayedFile<K> | undefined {
    return files[slide];
  };
}

export function Gallery<K = object>({
  drawings,
  addDrawing,
  changeGallery,
  removeFile: handleRemoveFile,
  rotateFile,
  editFile,
  setFiles,
  setGallery,
  disableMiniatures,
  preview,
  useMainDisplayedFile,
  useGalleryState,
  useDisplayedFiles,
  downloadFile,
  refreshPreview,
  openEditComponent,
  EditComponent,
  editTitleId,
  showActions,
  ImageDisplayOverlay,
  MiniaturesOverlay,
  mainImageHandlerRef,
  RightPanel,
  rightPanelControls,
  skipAutoSlideHandling,
  showPdfAsThumbnail,
  fetchShareableUrl,
}: GalleryProps<K>): ReactElement {
  const [filesLength] = useDisplayedFiles(lengthSelector);
  const [undeletedIndex] = useDisplayedFiles(undeletedIndexSelector);
  const [slide] = useGalleryState(slideSelector);
  const [isOpen] = useGalleryState(isOpenSelector);
  const [currentSlideFile] = useDisplayedFiles(
    currentSlideFileSelector(slide)
  );

  useEffect(() => {
    if (!filesLength && isOpen) {
      setGallery({ open: false, slide: 0 });
    }
  }, [filesLength, isOpen, setGallery]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const shouldTreatDeletedAsInvalid = !skipAutoSlideHandling;
    if (
      !currentSlideFile ||
      (shouldTreatDeletedAsInvalid && currentSlideFile.deleted)
    ) {
      if (undeletedIndex > -1) {
        setGallery({ open: true, slide: undeletedIndex });
        return;
      }

      setGallery({ open: false, slide: 0 });
    }
  }, [
    isOpen,
    currentSlideFile,
    setGallery,
    skipAutoSlideHandling,
    undeletedIndex,
  ]);

  useEffect(() => {
    if (!mainImageHandlerRef) return;

    return mainImageHandlerRef.current
      .getMainImageStore()
      .subscribe(() => {
        changeGallery('slide', 0);
      });
  }, [changeGallery, mainImageHandlerRef]);

  const drawing = drawings[slide];

  const [drawMode, setDrawMode] = useState(false);
  
  const moveGallery = useCallback(
    (direction: number) => {
      changeGallery(
        'slide',
        Math.min(filesLength - 1, Math.max(0, slide + direction))
      );
    },
    [slide, filesLength, changeGallery]
  );

  registerKeyboardHandlers(changeGallery, moveGallery);

  const handleSlidesAfterDeletion = useCallback(
    (deletedFileIdx: number) => {
      const willHaveNoFilesAfterDeletion = filesLength <= 1;
      if (willHaveNoFilesAfterDeletion) {
        changeGallery('open', false);
        return;
      }

      // UX rule:
      // - remove current file -> go to next item if available
      // - if removed file was last -> go to previous item
      const hasNextItem = deletedFileIdx < filesLength - 1;
      const targetSlideIndex = hasNextItem
        ? deletedFileIdx
        : clampPreviousSlideIndex(deletedFileIdx - 1);

      changeGallery('slide', targetSlideIndex);
    },
    [changeGallery, filesLength]
  );

  const removeFile = useCallback(
    (idx: number): void => {
      handleRemoveFile(idx);
      if (!skipAutoSlideHandling) {
        handleSlidesAfterDeletion(idx);
      }
    },
    [handleRemoveFile, handleSlidesAfterDeletion, skipAutoSlideHandling]
  );

  const actions: ActionsType = {
    removeFile,
    edit: editFile,
    rotateFile,
  };

  const ctx = useMemo(
    () => ({
      setFiles,
      drawings,
      addDrawing,
      changeGallery,
      setGallery,
      removeFile,
      rotateFile,
      editFile,
      disableMiniatures,
      preview,
      useDisplayedFiles,
      useMainDisplayedFile,
      useGalleryState,
      downloadFile,
      refreshPreview,
      openEditComponent,
      EditComponent,
      editTitleId,
      showActions,
      ImageDisplayOverlay,
      MiniaturesOverlay,
      rightPanelControls,
      showPdfAsThumbnail,
      fetchShareableUrl,
    }),
    [
      setFiles,
      drawings,
      addDrawing,
      changeGallery,
      setGallery,
      removeFile,
      rotateFile,
      editFile,
      disableMiniatures,
      preview,
      useDisplayedFiles,
      useMainDisplayedFile,
      useGalleryState,
      downloadFile,
      refreshPreview,
      openEditComponent,
      EditComponent,
      editTitleId,
      showActions,
      ImageDisplayOverlay,
      MiniaturesOverlay,
      rightPanelControls,
      showPdfAsThumbnail,
      fetchShareableUrl,
    ]
  );

  // react 18 types
  // @ts-ignore
  return createPortal(
    <GalleryContextProvider value={ctx}>
      {isOpen && (
        <Presentational
          drawing={drawing}
          changeGallery={changeGallery}
          moveGallery={moveGallery}
          drawings={drawings}
          idx={slide}
          actions={actions}
          setDrawMode={setDrawMode}
          drawMode={drawMode}
          addDrawing={addDrawing}
          disableMiniatures={disableMiniatures}
          preview={preview}
          MiniaturesOverlay={MiniaturesOverlay}
          RightPanel={RightPanel}
        />
      )}
      {EditComponent && <EditComponent />}
    </GalleryContextProvider>,
    document.body
  );
}

export function useGalleryContext<K = object>(): GalleryContextType<K> {
  return useAnyGalleryContext();
}
